import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Form from "react-bootstrap/Form";
import {
  FaUserPlus,
  FaUsers,
  FaIdCard,
  FaGraduationCap,
  FaCheckCircle,
  FaUpload,
  FaArrowLeft,
} from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import MyLoader from "../Spinner/MyLoader";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const STAFF_ROLES = [
  "Teacher",
  "Clerk",
  "Class Teacher",
  "Clerk And Techer",
  "Director And Principal",
  "Director And Voice Principal",
  "Faculty",
  "LDA",
  "UDA",
  "Tutor And LDA",
  "Academic Incharge",
  "Child Caretaker",
  "Mother Teacher",
  "Computer Operator",
  "Head Master",
  "Physical Teacher",
  "Office Bearers",
  "Driver",
  "Helper",
];

const LOGIN_TYPES = ["Admin", "Teacher", "Clerk", "None"];
const RELIGIONS = ["Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jain", "Other"];
const GENDERS = ["Male", "Female"];

const EMPTY_FORM = {
  staffName: "",
  department_id: "",
  dob: "",
  doj: "",
  stRegion: "",
  gender: "",
  AdharNo: "",
  nationality: "",
  staffRole: "",
  loginType: "",
  phone: "",
  email: "",
  staffAddress: "",
  degree: "",
  university: "",
  passingYear: "",
  percentage: "",
};

function Field({ label, required, children, hint, className = "" }) {
  return (
    <div className={`sr-field${className ? ` ${className}` : ""}`}>
      <label className="sr-field__label">
        {label}
        {required ? <span className="sr-field__req">*</span> : null}
      </label>
      {children}
      {hint ? <span className="sr-field__hint">{hint}</span> : null}
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="sr-section">
      <div className="sr-section__head">
        <span className="sr-section__icon" aria-hidden>
          <Icon />
        </span>
        <div className="min-w-0">
          <h2 className="sr-section__title">{title}</h2>
          {subtitle ? <p className="sr-section__sub">{subtitle}</p> : null}
        </div>
      </div>
      <div className="sr-section__body">{children}</div>
    </section>
  );
}

const StaffRegistration = () => {
  const loggedIn = localStorage.getItem("token") != null;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [mDepartment, setMDepartment] = useState([]);
  const [bootLoading, setBootLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const getAvatarInitials = () => {
    if (!formData.staffName) return "SR";
    const parts = formData.staffName.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || "";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase() || "SR";
  };

  const getDepartment = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setMDepartment([]);
      myToast.notify_warning("School code is missing.");
      return;
    }

    try {
      const response = await axios.get(`${API_BASE()}getDepartment.php`, {
        params: { school_code: schoolCode },
      });
      if (response.data.api_status === "OK") {
        setMDepartment(response.data.department || []);
      } else {
        myToast.notify_warning(response.data.api_remark || "Could not load departments.");
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      myToast.notify_warning("Network error loading departments.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBootLoading(true);
      await getDepartment();
      if (!cancelled) setBootLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [getDepartment]);

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const regChange = (e) => {
    updateField(e.target.id, e.target.value);
  };

  const isValidInput = () => {
    if (!formData.staffName?.trim()) {
      myToast.notify_warning("Please enter staff name.");
      return false;
    }
    if (!formData.phone?.trim()) {
      myToast.notify_warning("Please enter phone number.");
      return false;
    }
    if (!formData.email?.trim()) {
      myToast.notify_warning("Please enter email address.");
      return false;
    }
    return true;
  };

  const regSubmit = async () => {
    if (!isValidInput()) return;

    setSubmitting(true);
    const schoolCode = SessionManager.getSchoolCode();

    const qualification = [];
    if (
      formData.degree ||
      formData.university ||
      formData.passingYear ||
      formData.percentage
    ) {
      qualification.push({
        degree: formData.degree,
        university: formData.university,
        passing_year: formData.passingYear,
        percentage: formData.percentage,
      });
    }

    try {
      const response = await axios.get(`${API_BASE()}registerStaff.php`, {
        params: {
          school_code: schoolCode,
          StaffName: formData.staffName,
          Department_id: formData.department_id,
          dob: formData.dob,
          doj: formData.doj,
          religion: formData.stRegion,
          gender: formData.gender,
          aadhar_no: formData.AdharNo,
          nationality: formData.nationality,
          role: formData.staffRole,
          login_type: formData.loginType,
          address: formData.staffAddress,
          phone: formData.phone,
          email: formData.email,
          qualification: JSON.stringify(qualification),
        },
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Staff registered successfully.");
        window.location.href = "/AdminMain/StaffList/MDL012";
      } else {
        myToast.notify_warning(response.data?.api_remark || "Registration failed.");
      }
    } catch (error) {
      console.error("Error in registration:", error);
      myToast.notify_warning("Network error during registration.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!loggedIn) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <h2>Access Denied</h2>
            <p>You must be logged in to view this page.</p>
            <Link to="/Login" className="btn btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (bootLoading) return <MyLoader />;

  const selectedDepartmentName =
    mDepartment.find((d) => String(d.department_id) === String(formData.department_id))
      ?.department_name || "";

  return (
    <div
      className="content payroll-page sm-page sr-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Staff Registration</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUserPlus />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Register New Staff</h1>
              <p className="sm-hero__sub">
                Add a staff profile for attendance, payroll, and access
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link
              to="/AdminMain/StaffManagement"
              className="sr-hero-link"
            >
              <FaArrowLeft aria-hidden /> Staff Hub
            </Link>
            <Link
              to="/AdminMain/StaffList/MDL012"
              className="sr-hero-link sr-hero-link--solid"
            >
              <FaUsers aria-hidden /> Staff List
            </Link>
          </div>
        </header>

        <div className="sr-body">
          <div className="sr-profile-strip">
            <div className="sr-avatar" aria-hidden>
              {getAvatarInitials()}
            </div>
            <div className="sr-profile-strip__meta min-w-0">
              <div className="sr-profile-strip__name">
                {formData.staffName?.trim() || "New staff member"}
              </div>
              <div className="sr-profile-strip__role">
                {[formData.staffRole, selectedDepartmentName]
                  .filter(Boolean)
                  .join(" · ") || "Fill personal details below"}
              </div>
            </div>
            <label className="sr-upload-btn">
              <FaUpload aria-hidden />
              Upload Photo
              <input type="file" name="upload photo" accept="image/*" hidden />
            </label>
          </div>

          <SectionCard
            icon={FaIdCard}
            title="Personal Information"
            subtitle="Basic details used across attendance and payroll"
          >
            <div className="sr-stack">
              <div className="sr-grid sr-grid--1">
                <Field label="Staff Name" required>
                  <input
                    type="text"
                    id="staffName"
                    placeholder="Enter full name"
                    value={formData.staffName}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>
              </div>

              <div className="sr-grid sr-grid--4">
                <Field label="Department">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) => updateField("department_id", e.target.value)}
                    value={formData.department_id}
                  >
                    <option value="">Select department</option>
                    {mDepartment.map((item, index) => (
                      <option
                        key={item.department_id || index}
                        value={item.department_id}
                      >
                        {item.department_name}
                      </option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Staff Role">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) => updateField("staffRole", e.target.value)}
                    value={formData.staffRole}
                  >
                    <option value="">Select role</option>
                    {STAFF_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Login Type">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) => updateField("loginType", e.target.value)}
                    value={formData.loginType}
                  >
                    <option value="">Select login type</option>
                    {LOGIN_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Religion">
                  <Form.Select
                    className="sr-input sr-select"
                    onChange={(e) => updateField("stRegion", e.target.value)}
                    value={formData.stRegion}
                  >
                    <option value="">Select religion</option>
                    {RELIGIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Form.Select>
                </Field>
              </div>

              <div className="sr-grid sr-grid--4">
                <Field label="Phone Number" required>
                  <input
                    type="tel"
                    id="phone"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Email Address" required>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Date of Birth">
                  <input
                    type="date"
                    id="dob"
                    value={formData.dob}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Date of Joining">
                  <input
                    type="date"
                    id="doj"
                    value={formData.doj}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>
              </div>

              <div className="sr-grid sr-grid--3">
                <Field label="Gender">
                  <div className="sr-chip-row" role="group" aria-label="Gender">
                    {GENDERS.map((g) => (
                      <button
                        key={g}
                        type="button"
                        className={`sr-choice-chip${
                          formData.gender === g ? " is-active" : ""
                        }`}
                        onClick={() => updateField("gender", g)}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Aadhaar Number">
                  <input
                    type="text"
                    id="AdharNo"
                    placeholder="Enter Aadhaar number"
                    value={formData.AdharNo}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>

                <Field label="Nationality">
                  <input
                    type="text"
                    id="nationality"
                    placeholder="Enter nationality"
                    value={formData.nationality}
                    onChange={regChange}
                    className="sr-input"
                  />
                </Field>
              </div>

              <div className="sr-grid sr-grid--1">
                <Field label="Staff Address">
                  <textarea
                    id="staffAddress"
                    rows={3}
                    className="sr-input sr-textarea"
                    placeholder="Enter full address"
                    value={formData.staffAddress}
                    onChange={regChange}
                  />
                </Field>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={FaGraduationCap}
            title="Education & Qualification"
            subtitle="Optional academic details"
          >
            <div className="sr-grid">
              <Field label="Degree / Diploma">
                <input
                  type="text"
                  id="degree"
                  className="sr-input"
                  placeholder="Highest degree or diploma"
                  value={formData.degree}
                  onChange={regChange}
                />
              </Field>
              <Field label="University / Institute">
                <input
                  type="text"
                  id="university"
                  className="sr-input"
                  placeholder="Name of university or institute"
                  value={formData.university}
                  onChange={regChange}
                />
              </Field>
              <Field label="Passing Year">
                <input
                  type="text"
                  id="passingYear"
                  className="sr-input"
                  placeholder="e.g. 2020"
                  value={formData.passingYear}
                  onChange={regChange}
                />
              </Field>
              <Field label="Percentage">
                <input
                  type="text"
                  id="percentage"
                  className="sr-input"
                  placeholder="e.g. 75%"
                  value={formData.percentage}
                  onChange={regChange}
                />
              </Field>
            </div>
          </SectionCard>

          <div className="sr-actions">
            <Link to="/AdminMain/StaffManagement" className="sr-btn sr-btn--ghost">
              Cancel
            </Link>
            <button
              type="button"
              className="sr-btn sr-btn--primary"
              onClick={regSubmit}
              disabled={submitting}
            >
              <FaCheckCircle aria-hidden />
              {submitting ? "Saving…" : "Save Staff Profile"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffRegistration;
